import './styles.css'

import {
  memo, useEffect, useRef, useState,
} from 'react'

import { AnimatedSpinner } from '../../../components/AnimatedSpinner'
import { AngleIcon } from '../../../components/Icons'
import { useClickOutside } from '../../../hooks/useClickOutside'

interface ISearchableInputProps {
  searchResult: string[]
  onSearchChange: (query: string) => void
  isFetching?: boolean
  wrapperClassName?: string
  placeholder?: string
  onSelect: (option: string) => void
  needFetchNextPage: boolean
  fetchNextPage: () => void
  defaultValue?: string
  disabled?: boolean
}

const SearchableInput: React.FC<ISearchableInputProps> = memo(
  ({
    searchResult,
    onSearchChange,
    isFetching,
    wrapperClassName = '',
    placeholder = '',
    onSelect,
    needFetchNextPage,
    fetchNextPage,
    defaultValue,
    disabled,
  }) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)

    const [inputValue, setInputValue] = useState<string>('')
    const [isOpen, setIsOpen] = useState<boolean>(false)

    useEffect(() => {
      if (defaultValue) {
        setInputValue(defaultValue)
        onSearchChange(defaultValue)
      }
    }, [defaultValue])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value)
      onSearchChange(e.target.value)
      setIsOpen(true)
    }

    useClickOutside(containerRef, () => {
      setIsOpen(false)
    })

    const handleSelectOption = (option: string) => {
      setInputValue(option)
      onSelect(option)
      setIsOpen(false)
      inputRef.current?.blur()
    }

    useEffect(() => {
      const el = dropdownRef.current
      if (!el) return
      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = el
        const nearBottom = scrollTop + clientHeight >= scrollHeight - 50
        if (nearBottom && needFetchNextPage) {
          fetchNextPage()
        }
      }
      el.addEventListener('scroll', handleScroll)
      return () => el.removeEventListener('scroll', handleScroll)
    }, [isOpen, searchResult.length])

    const clearInput = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      setInputValue('')
      setIsOpen(false)
      onSelect('')
      inputRef.current?.focus()
    }

    const handleInputClick = () => {
      setIsOpen(prev => !prev)
    }
    if (disabled) {
      return (
        <div className={wrapperClassName}>
          <input
            className="w-full border border-gray-300 rounded p-2 text-black"
            value={defaultValue}
            placeholder={placeholder}
            disabled
          />
        </div>
      )
    }

    return (
      <div ref={containerRef} className="relative">
        <div onClick={handleInputClick} className={wrapperClassName}>
          <input
            className="w-full border border-gray-300 rounded p-2 text-black"
            onChange={handleInputChange}
            value={inputValue}
            placeholder={placeholder}
            ref={inputRef}
          />
          {isFetching && (
            <div className="me-2">
              <AnimatedSpinner />
            </div>
          )}
          {inputValue && (
            <button onClick={clearInput}>
              <i className="fa-solid fa-xmark text-xl me-2" />
            </button>
          )}
          <span className="tc-mv-select-selectbox-arrow me-3">
            <AngleIcon />
          </span>
        </div>
        {isOpen && !!searchResult.length && (
          <div className="px-4 py-4 tc-mv-search-input-result">
            <div ref={dropdownRef} className="h-full w-full overflow-auto">
              {searchResult.map(option => (
                <div
                  key={option}
                  className={`rounded-xl text-sm w-full break-words cursor-pointer hover:bg-black hover:bg-opacity-20 px-4 py-[10px]
              }`}
                  onClick={() => {
                    handleSelectOption(option)
                  }}
                >
                  {option}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  },
)

export { SearchableInput }
